import { useCallback, useEffect, useRef } from 'react';
import { usePTZStore } from '@/features/camera/store/usePTZStore';
import { usePTZSocket } from '@/shared/hooks/websocket/usePTZSocket';
import { wrapDegrees } from '@c360/shared';
import { PTZValues } from '../types';

// The player's PTZ control is a velocity, not a position: every frame in
// manual mode it adds `input * sensitivity` to its goal (c360-lead
// player/targeting.rs, PAN/TILT_SENSITIVITY = 50, ZOOM_SENSITIVITY = 0.1,
// tilt inverted), and the vcam eases toward the goal through its own PID.
// The vcam the state socket reports trails what was commanded by about
// 0.2 s, so steering on it naively surges, overshoots and creeps back.
//
// So the speed follows a profile: eased starts and stops (acceleration
// limit), a top speed, and a braking curve toward the target (the speed
// from which it can still stop in time, then proportional for the last
// fraction of a degree). Each tick looks at where the vcam will be once what
// has already been commanded shows up, not where it is, so it brakes before
// it arrives.
//
// This only moves the preview. The clip's pan and tilt are aimed by the API
// from the preset, so the export is exact even if the preview stops a hair
// short. The
// player already has a static-target mode (TrackingTarget::Spot) that no
// control message reaches yet; a "track this point" message would replace it.
const TICK_MS = 33;
const TOLERANCE_DEG = 0.05;
const SETTLED_TICKS = 4;
const TIMEOUT_MS = 8000;
// The player's guardrails (on by default) keep the frame on the sensor, so
// they can hold the vcam short of a goal it can't reach (a target high above
// the ice at a wide zoom). Once it has stopped moving short of the goal for
// this long, give up rather than push until the timeout.
const STALL_GRACE_MS = 600;
const STALL_TICKS = 10;
// Commanded motion shows up in the reported vcam this much later.
const LAG_S = 0.2;
// Below this distance the speed is proportional to it, which keeps the
// approach from chattering (the braking curve is infinitely steep at zero).
const FINAL_GAIN_PER_S = 2;
// The player advances its goal once per frame. On a dev player it moved
// about two thirds of what 60 fps predicted, so plan for 40; the loop is
// closed on the reported vcam, so this only sets how eager the steering is.
const PLAYER_FPS = 40;
const SENSITIVITY = { pan: 50, tilt: -50, zoom: 0.1 } as const;
/** Top speed and acceleration per axis, in degrees per second (squared). */
const LIMITS = {
  pan: { speed: 35, accel: 60 },
  tilt: { speed: 25, accel: 45 },
  zoom: { speed: 15, accel: 30 },
} as const;
const AXES = ['pan', 'tilt', 'zoom'] as const;
type Axis = (typeof AXES)[number];

const diff = (axis: Axis, to: number, from: number) =>
  axis === 'pan' ? wrapDegrees(to - from) : to - from;
const zero = () => ({ pan: 0, tilt: 0, zoom: 0 });

/** A goal without zoom leaves the zoom where it is. */
export type DriveGoal = Pick<PTZValues, 'pan' | 'tilt'> & { zoom?: number };

interface Drive {
  goal: DriveGoal;
  /** Commanded speed, degrees per second. */
  velocity: PTZValues;
  /** Smoothed speed of the reported vcam, degrees per second. */
  measured: PTZValues;
  lastVcam: PTZValues;
  startedAt: number;
  lastTick: number;
  settledTicks: number;
  stalledTicks: number;
}

export const useDriveToPTZ = () => {
  const { getCurrentPTZ } = usePTZStore();
  const { setValue } = usePTZSocket();
  // Both hooks hand out new functions every render. Read them through refs so
  // driveTo stays stable: a caller that re-aims whenever driveTo changes would
  // otherwise restart the drive on every render.
  const readRef = useRef(getCurrentPTZ);
  readRef.current = getCurrentPTZ;
  // usePTZSocket hands out a new setValue every render; a drive outlives many
  // renders (the clip save re-renders the panel), so it reads the latest one.
  const sendRef = useRef(setValue);
  sendRef.current = setValue;
  const timerRef = useRef<number | null>(null);
  const settleRef = useRef<((settled: boolean) => void) | null>(null);
  const driveRef = useRef<Drive | null>(null);
  const lastSentRef = useRef<PTZValues>(zero());

  const send = useCallback((input: PTZValues) => {
    const last = lastSentRef.current;
    if (AXES.every((a) => Math.abs(input[a] - last[a]) < 1e-6)) return;
    lastSentRef.current = input;
    sendRef.current(input, { automated: true });
  }, []);

  const stop = useCallback(
    (settled = false) => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
        lastSentRef.current = { pan: NaN, tilt: NaN, zoom: NaN };
        send(zero());
      }
      driveRef.current = null;
      settleRef.current?.(settled);
      settleRef.current = null;
    },
    [send],
  );

  useEffect(() => () => stop(), [stop]);

  const tick = useCallback(() => {
    const drive = driveRef.current;
    const vcam = readRef.current?.();
    if (!drive || !vcam) return;
    const now = performance.now();
    const dt = Math.min((now - drive.lastTick) / 1000, 0.1);
    drive.lastTick = now;
    if (now - drive.startedAt > TIMEOUT_MS) return stop(false);
    if (dt <= 0) return;

    let inTolerance = true;
    const input = zero();
    for (const axis of AXES) {
      // The state socket updates slower than this loop, so smooth the speed.
      const step = diff(axis, vcam[axis], drive.lastVcam[axis]) / dt;
      drive.measured[axis] += (step - drive.measured[axis]) * Math.min(1, dt / 0.1);
      const goal = drive.goal[axis];
      if (goal === undefined) continue;
      const error = diff(axis, goal, vcam[axis]);
      if (Math.abs(error) >= TOLERANCE_DEG) inTolerance = false;
      // Predict with the commanded speed: it is smooth and known, where the
      // reported one is noisy and itself late.
      const ahead = error - drive.velocity[axis] * LAG_S;
      const { speed, accel } = LIMITS[axis];
      const wanted =
        Math.sign(ahead) *
        Math.min(speed, Math.sqrt(2 * accel * Math.abs(ahead)), Math.abs(ahead) * FINAL_GAIN_PER_S);
      const change = Math.max(-accel * dt, Math.min(accel * dt, wanted - drive.velocity[axis]));
      drive.velocity[axis] += change;
      input[axis] = drive.velocity[axis] / (PLAYER_FPS * SENSITIVITY[axis]);
    }
    drive.lastVcam = { pan: vcam.pan, tilt: vcam.tilt, zoom: vcam.zoom };

    const still = AXES.every(
      (a) => drive.goal[a] === undefined || Math.abs(drive.measured[a]) < 0.2,
    );
    drive.settledTicks = inTolerance && still ? drive.settledTicks + 1 : 0;
    if (drive.settledTicks >= SETTLED_TICKS) return stop(true);
    const stalled = !inTolerance && still && now - drive.startedAt > STALL_GRACE_MS;
    drive.stalledTicks = stalled ? drive.stalledTicks + 1 : 0;
    if (drive.stalledTicks >= STALL_TICKS) return stop(false);
    send(inTolerance ? zero() : input);
  }, [send, stop]);

  /**
   * Resolves true once the preview is within tolerance, false on timeout, when
   * the vcam stalls short of the goal, or when a newer goal replaces it. A drive already under way keeps its speed
   * and just turns toward the new goal, so following a target as it is
   * dragged stays smooth.
   */
  const driveTo = useCallback(
    (target: DriveGoal) =>
      new Promise<boolean>((resolve) => {
        settleRef.current?.(false);
        settleRef.current = resolve;
        const now = performance.now();
        const drive = driveRef.current;
        if (drive) {
          // Keep the current speed and turn toward the new goal.
          drive.goal = { ...target };
          drive.startedAt = now;
          drive.settledTicks = 0;
          drive.stalledTicks = 0;
          return;
        }
        const vcam = readRef.current?.();
        if (!vcam) return resolve(false);
        driveRef.current = {
          goal: { ...target },
          velocity: zero(),
          measured: zero(),
          lastVcam: { pan: vcam.pan, tilt: vcam.tilt, zoom: vcam.zoom },
          startedAt: now,
          lastTick: now,
          settledTicks: 0,
          stalledTicks: 0,
        };
        lastSentRef.current = zero();
        timerRef.current = window.setInterval(tick, TICK_MS);
        tick();
      }),
    [tick],
  );

  const cancel = useCallback(() => stop(), [stop]);

  return { driveTo, stop: cancel };
};
